import { createContext, useReducer, useEffect } from 'react';
import { supabase } from '../supabase/config';
import { useAuth } from '../hooks/useAuth';

export const FriendsContext = createContext();

const friendsReducer = (state, action) => {
  switch (action.type) {
    case 'SET_FRIENDS':
      return { ...state, friends: action.payload };
    case 'SET_FRIEND_REQUESTS':
      return { ...state, friendRequests: action.payload };
    case 'SET_NOTIFICATIONS':
      return { ...state, notifications: action.payload };
    case 'ADD_FRIEND_REQUEST':
      return { 
        ...state, 
        friendRequests: [...state.friendRequests, action.payload] 
      };
    case 'UPDATE_FRIEND_REQUEST':
      return { 
        ...state, 
        friendRequests: state.friendRequests.map(req => 
          req.id === action.payload.id ? action.payload : req
        ) 
      };
    case 'ADD_FRIEND':
      return { 
        ...state, 
        friends: [...state.friends, action.payload] 
      };
    case 'MARK_NOTIFICATION_READ':
      return { 
        ...state, 
        notifications: state.notifications.map(notif => 
          notif.id === action.payload ? { ...notif, is_read: true } : notif
        ) 
      };
    default:
      return state;
  }
};

export const FriendsContextProvider = ({ children }) => {
  const { user } = useAuth();
  const [state, dispatch] = useReducer(friendsReducer, {
    friends: [],
    friendRequests: [],
    notifications: []
  });

  // Fetch friends and friend requests when user changes
  useEffect(() => {
    if (!user) return;

    const fetchFriends = async () => {
      try {
        // Fetch accepted friends
        const { data: acceptedFriends, error: friendsError } = await supabase
          .from('friends')
          .select('*, friend:friend_id(*)')
          .eq('user_id', user.id)
          .eq('status', 'accepted');

        if (friendsError) throw friendsError;

        // Also fetch friends where the user is the friend_id
        const { data: reverseFriends, error: reverseFriendsError } = await supabase
          .from('friends')
          .select('*, friend:user_id(*)')
          .eq('friend_id', user.id)
          .eq('status', 'accepted');

        if (reverseFriendsError) throw reverseFriendsError;

        // Combine both sets of friends
        const allFriends = [
          ...acceptedFriends.map(f => ({ ...f, friend: f.friend })),
          ...reverseFriends.map(f => ({ ...f, friend: f.friend }))
        ];

        dispatch({ type: 'SET_FRIENDS', payload: allFriends });
      } catch (error) {
        console.error('Error fetching friends:', error);
      }
    };

    const fetchFriendRequests = async () => {
      try {
        // Fetch pending friend requests sent to the user
        const { data: incomingRequests, error: incomingError } = await supabase
          .from('friends')
          .select('*, requester:user_id(*)')
          .eq('friend_id', user.id)
          .eq('status', 'pending');

        if (incomingError) throw incomingError;

        // Fetch pending friend requests sent by the user
        const { data: outgoingRequests, error: outgoingError } = await supabase
          .from('friends')
          .select('*, recipient:friend_id(*)')
          .eq('user_id', user.id)
          .eq('status', 'pending');

        if (outgoingError) throw outgoingError;

        const allRequests = [
          ...incomingRequests.map(req => ({ ...req, direction: 'incoming' })),
          ...outgoingRequests.map(req => ({ ...req, direction: 'outgoing' }))
        ];

        dispatch({ type: 'SET_FRIEND_REQUESTS', payload: allRequests });
      } catch (error) {
        console.error('Error fetching friend requests:', error);
      }
    };

    const fetchNotifications = async () => {
      try {
        const { data, error } = await supabase
          .from('notifications')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });

        if (error) throw error;

        dispatch({ type: 'SET_NOTIFICATIONS', payload: data });
      } catch (error) {
        console.error('Error fetching notifications:', error);
      }
    };

    fetchFriends();
    fetchFriendRequests();
    fetchNotifications();

    // Set up real-time subscriptions
    const friendsSubscription = supabase
      .channel('friends-changes')
      .on('postgres_changes', 
        { 
          event: '*', 
          schema: 'public', 
          table: 'friends',
          filter: `user_id=eq.${user.id}` 
        }, 
        () => {
          fetchFriends();
          fetchFriendRequests();
        }
      )
      .on('postgres_changes', 
        { 
          event: '*', 
          schema: 'public', 
          table: 'friends',
          filter: `friend_id=eq.${user.id}` 
        }, 
        () => {
          fetchFriends();
          fetchFriendRequests();
        }
      )
      .subscribe();

    const notificationsSubscription = supabase
      .channel('notifications-changes')
      .on('postgres_changes', 
        { 
          event: '*', 
          schema: 'public', 
          table: 'notifications',
          filter: `user_id=eq.${user.id}` 
        }, 
        () => {
          fetchNotifications();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(friendsSubscription);
      supabase.removeChannel(notificationsSubscription);
    };
  }, [user]);

  // Send a friend request
  const sendFriendRequest = async (friendEmail) => {
    try {
      // First, find the user by email
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('id')
        .eq('email', friendEmail)
        .single();

      if (userError) throw userError;
      if (!userData) throw new Error('User not found');

      // Check if a friend request already exists
      const { data: existingRequest, error: checkError } = await supabase
        .from('friends')
        .select('*')
        .or(`user_id.eq.${user.id},friend_id.eq.${user.id}`)
        .or(`user_id.eq.${userData.id},friend_id.eq.${userData.id}`);

      if (checkError) throw checkError;
      if (existingRequest && existingRequest.length > 0) {
        throw new Error('A friend request already exists between these users');
      }

      // Create the friend request
      const { data, error } = await supabase
        .from('friends')
        .insert([
          { 
            user_id: user.id, 
            friend_id: userData.id, 
            status: 'pending' 
          }
        ])
        .select()
        .single();

      if (error) throw error;

      dispatch({ 
        type: 'ADD_FRIEND_REQUEST', 
        payload: { ...data, direction: 'outgoing', recipient: { id: userData.id } } 
      });

      return data;
    } catch (error) {
      throw error;
    }
  };

  // Respond to a friend request
  const respondToFriendRequest = async (requestId, accept) => {
    try {
      const { data, error } = await supabase
        .from('friends')
        .update({ 
          status: accept ? 'accepted' : 'rejected',
          updated_at: new Date().toISOString()
        })
        .eq('id', requestId)
        .select()
        .single();

      if (error) throw error;

      dispatch({ 
        type: 'UPDATE_FRIEND_REQUEST', 
        payload: data 
      });

      if (accept) {
        // Also add to friends list if accepted
        dispatch({
          type: 'ADD_FRIEND',
          payload: data
        });
      }

      return data;
    } catch (error) {
      throw error;
    }
  };

  // Mark a notification as read
  const markNotificationRead = async (notificationId) => {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('id', notificationId);

      if (error) throw error;

      dispatch({ type: 'MARK_NOTIFICATION_READ', payload: notificationId });
    } catch (error) {
      throw error;
    }
  };

  return (
    <FriendsContext.Provider value={{ 
      ...state, 
      sendFriendRequest, 
      respondToFriendRequest,
      markNotificationRead
    }}>
      {children}
    </FriendsContext.Provider>
  );
}; 